import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, object = logged in
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    // Never clobber a session already set by login() while this bootstrap is in flight.
    const keepOr = (prev, next) => (prev && typeof prev === "object" ? prev : next);
    try {
      const { data } = await api.get("/auth/me", { skipAuthRefresh: true });
      setUser((prev) => keepOr(prev, data));
    } catch {
      // Access token may have expired — try to refresh using the refresh cookie.
      try {
        await api.post("/auth/refresh", null, { skipAuthRefresh: true });
        const { data } = await api.get("/auth/me", { skipAuthRefresh: true });
        setUser((prev) => keepOr(prev, data));
      } catch {
        setUser((prev) => keepOr(prev, false));
      }
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: GoogleCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      const onExpired = () => setUser(false);
      window.addEventListener("auth:sessionexpired", onExpired);
      return () => window.removeEventListener("auth:sessionexpired", onExpired);
    }
    refresh();
    const onExpired = () => setUser(false);
    window.addEventListener("auth:sessionexpired", onExpired);
    return () => window.removeEventListener("auth:sessionexpired", onExpired);
  }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("logout:", e?.message); }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
